import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { registerToastDispatcher, type ToastItem, type ToastType } from '../../utils/toast';

const TOAST_DURATION_MS = 4000;

function ToastCard({ item }: { item: ToastItem }) {
    const isSuccess = item.type === 'success';

    return (
        <div
            role="status"
            className={`pointer-events-auto flex items-start gap-2.5 min-w-[280px] max-w-sm px-4 py-3 rounded-xl border shadow-xl ${
                isSuccess
                    ? 'bg-[#f0fdf4] border-[#bbf7d0] text-[#15803d]'
                    : 'bg-[#fef2f2] border-[#fecaca] text-[#ef4444]'
            }`}
        >
            <svg
                className="w-5 h-5 shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
            >
                {isSuccess ? (
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                    />
                ) : (
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                )}
            </svg>
            <p className="text-sm font-medium leading-snug">{item.message}</p>
        </div>
    );
}

// CPM design (Master Editor.dc.html `toastStyle`/`toastOk`/`toastErr`): fixed bottom-center
// pill, solid tone fill (not a light tint), white icon + text, explicit Dismiss link.
// Design only ever tracks a single toast string — no stacking — so only the latest shows.
function CpmToastCard({ item, onDismiss }: { item: ToastItem; onDismiss: () => void }) {
    const isSuccess = item.type === 'success';

    return (
        <div
            role="status"
            className="pointer-events-auto flex items-center"
            style={{
                gap: 14,
                padding: '12px 16px',
                borderRadius: 12,
                background: isSuccess
                    ? 'var(--cpm-success-text-light)'
                    : 'var(--cpm-shortfall-text)',
                boxShadow: '0 18px 40px -12px rgba(14,8,88,.55)',
                fontFamily: 'var(--cpm-font-sans), Inter, system-ui, sans-serif',
                animation: 'cpm-pop-in 160ms ease-out',
            }}
        >
            <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#fff"
                strokeWidth={isSuccess ? 3 : 2.4}
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ flex: 'none' }}
            >
                {isSuccess ? (
                    <path d="M20 6 9 17l-5-5" />
                ) : (
                    <>
                        <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
                        <path d="M12 9v4" />
                        <path d="M12 17h.01" />
                    </>
                )}
            </svg>
            <span style={{ fontSize: 13, fontWeight: 800, color: '#fff' }}>{item.message}</span>
            <button
                type="button"
                onClick={onDismiss}
                style={{
                    cursor: 'pointer',
                    fontSize: 12.5,
                    fontWeight: 700,
                    color: 'rgba(255,255,255,.6)',
                    background: 'transparent',
                    border: 0,
                    padding: 0,
                }}
            >
                Dismiss
            </button>
        </div>
    );
}

interface ToastProviderProps {
    children: ReactNode;
}

export default function ToastProvider({ children }: ToastProviderProps) {
    const [items, setItems] = useState<ToastItem[]>([]);
    const { pathname } = useLocation();
    const isCpm = pathname.startsWith('/central-product-master');

    const pushToast = useCallback((type: ToastType, message: string) => {
        const id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
        setItems((prev) => [...prev, { id, type, message }]);

        window.setTimeout(() => {
            setItems((prev) => prev.filter((item) => item.id !== id));
        }, TOAST_DURATION_MS);
    }, []);

    useEffect(() => {
        registerToastDispatcher(pushToast);
        return () => registerToastDispatcher(null);
    }, [pushToast]);

    const dismiss = (id: string) => setItems((prev) => prev.filter((item) => item.id !== id));

    // CPM's design has no stacking concept — only the latest message is ever shown.
    const cpmItem = items.length > 0 ? items[items.length - 1] : null;

    const toastLayer =
        isCpm && cpmItem
            ? createPortal(
                  <div
                      aria-live="polite"
                      aria-atomic="true"
                      className="cpm-theme fixed left-1/2"
                      style={{ bottom: 26, transform: 'translateX(-50%)', zIndex: 80 }}
                  >
                      <CpmToastCard item={cpmItem} onDismiss={() => dismiss(cpmItem.id)} />
                  </div>,
                  document.body
              )
            : !isCpm && items.length > 0
              ? createPortal(
                    <div
                        aria-live="polite"
                        aria-atomic="true"
                        className="fixed top-4 right-4 flex flex-col gap-2 pointer-events-none"
                        style={{ zIndex: 2147483647 }}
                    >
                        {items.map((item) => (
                            <ToastCard key={item.id} item={item} />
                        ))}
                    </div>,
                    document.body
                )
              : null;

    return (
        <>
            {children}
            {toastLayer}
        </>
    );
}
